import React, { useCallback, useContext, useEffect, useState } from "react";
import { Button, Input, Table, Tooltip } from "antd";
import { PlusOutlined, EditOutlined, SettingOutlined } from "@ant-design/icons";
import axios from "axios";
import CommissaryLayout from "../../layout/CommissaryLayout";
import CommissaryAddRaw from "./CommissaryAddRaw";
import { UserInfo } from "../../../context";
import CommissaryRawEdit from "./CommissaryRawEdit";
import CommissaryViewSetStandards from "./CommissaryViewSetStandards";

const CommissaryInventory = () => {
  const [productRawList, setProductRawList] = useState({
    data: [],
    ready: false,
  });
  const [unitList, setUnitList] = useState({ data: [], ready: false });
  const [categoryList, setCategoryList] = useState({ data: [], ready: false });
  const [loading, setLoading] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [setStandardsDrawer, setSetStandardsDrawer] = useState(false);

  const tableLayout = "";
  const scroll = {};
  const { userData } = useContext(UserInfo);
  const [editDrawer, setEditDrawer] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [search, setSearch] = useState("");

  const columns = [
    {
      title: "SKU",
      dataIndex: "sku",
      sorter: (a, b) => a.sku - b.sku,
      render: (text, record) => <p>{text}</p>,
    },
    {
      title: "Product",
      dataIndex: "product",
      render: (text, record) => <p>{text}</p>,
    },
    {
      title: "Description",
      dataIndex: "description",
      render: (text, record) => <p>{text}</p>,
    },
    {
      title: "Unit",
      // dataIndex: "unit",
      dataIndex: ["rawproductsuserunit", "unit"],
      filters: [
        {
          text: "Piece(s)",
          value: "Piece(s)",
        },
        {
          text: "Bottle(s)",
          value: "Bottle(s)",
        },
        {
          text: "Vial(s)",
          value: "Vial(s)",
        },
        {
          text: "Box(s)",
          value: "Box(s)",
        },
      ],
      onFilter: (value, record) =>
        record.rawproductsuserunit.unit.includes(value),
      ellipsis: true,
      render: (text, record) => <p>{text}</p>,
    },
    {
      title: "Purchase Price",
      dataIndex: "purchase_price",
      align: "right",
      render: (text, record) => <p>{text}</p>,
    },
    {
      title: "MSRP",
      dataIndex: "msrp",
      align: "right",
      render: (text, record) => <p>{text}</p>,
    },
    {
      title: "SRP",
      dataIndex: "srp",
      align: "right",
      render: (text, record) => <p>{text}</p>,
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      align: "right",
      render: (text, record) => <p>{text}</p>,
    },
    {
      title: "",
      dataIndex: "",
      align: "center",
      render: (text) => (
        <p>
          <Tooltip title="Edit">
            <Button
              type="dashed"
              shape="circle"
              onClick={() => {
                setSelectedData(text);
                setEditDrawer(true);
              }}
              icon={<EditOutlined />}
            />
          </Tooltip>

          {parseInt(text.is_production) === 1 && (
            <Tooltip title="Set standards">
              <Button
                type="dashed"
                shape="circle"
                onClick={() => {
                  setSelectedData(text);
                  setSetStandardsDrawer(true);
                }}
                icon={<SettingOutlined />}
                style={{ marginLeft: 5 }}
              />
            </Tooltip>
          )}
        </p>
      ),
    },
  ];

  const tableProps = {
    bordered: false,
    loading,
    size: "medium",
    // expandable,
    showHeader: true,
    scroll,
    tableLayout,
  };

  const tableColumns = columns.map((item) => ({
    ...item,
    ellipsis: false,
  }));

  const getRawProductList = useCallback(() => {
    var params = { user_id: userData.data.id, is_production: "all" };
    axios
      .get("user/commissary/raw-product-list", {
        params,
      })
      .then((response) => {
        const data = response.data;
        setProductRawList({ data, ready: true });
      })
      .catch((error) => {
        console.log("ERROR: ", error);
      });
  }, [userData.data.id]);

  const getAllUnits = useCallback(() => {
    var params = { user_id: userData.data.id };
    axios
      .get("user/commissary/unit-list", { params })
      .then((response) => {
        const data = response.data;
        setUnitList({ data, ready: true });
      })
      .catch((error) => {
        console.log("ERROR: ", error);
      });
  }, [userData.data.id]);

  const getCommissaryCategory = useCallback(() => {
    var params = { user_id: userData.data.id };
    axios
      .get("user/commissary/category-list", { params })
      .then((response) => {
        const data = response.data;
        setCategoryList({ data, ready: true });
      })
      .catch((error) => {
        console.log("ERROR: ", error);
      });
  }, [userData.data.id]);

  useEffect(() => {
    getRawProductList();
    getAllUnits();
    getCommissaryCategory();
  }, [getRawProductList, getAllUnits, getCommissaryCategory]);

  const searchable = productRawList.data.filter((data) => {
    return data.product.toLowerCase().indexOf(search.toLowerCase()) >= 0;
  });

  return (
    <CommissaryLayout>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginBottom: 5,
        }}
      >
        <div style={{ flexGrow: 1 }}>
          <Input
            placeholder="Search raw"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              border: "2px solid #e5e5e5",
              width: window.innerWidth < 576 ? "100%" : 300,
            }}
            size="large"
          />
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setOpenDrawer(true)}
        >
          Raw Product
        </Button>
      </div>

      <div style={{ overflowX: "auto" }}>
        <Table
          {...tableProps}
          pagination={{
            position: ["bottomCenter"],
          }}
          columns={tableColumns}
          dataSource={searchable.length > 0 ? searchable : []}
          scroll={scroll}
          rowKey={(record) => record.id}
        />
      </div>

      {openDrawer && (
        <CommissaryAddRaw
          open={openDrawer}
          close={() => setOpenDrawer(false)}
          setLoading={setLoading}
          getRawProductList={() => getRawProductList()}
          unitList={unitList}
          categoryList={categoryList}
        />
      )}

      {editDrawer && (
        <CommissaryRawEdit
          open={editDrawer}
          close={() => setEditDrawer(false)}
          setLoading={setLoading}
          getRawProductList={() => getRawProductList()}
          selectedData={selectedData}
          unitList={unitList}
          categoryList={categoryList}
        />
      )}

      {setStandardsDrawer && (
        <CommissaryViewSetStandards
          open={setStandardsDrawer}
          close={() => setSetStandardsDrawer(false)}
          selectedData={selectedData}
        />
      )}
    </CommissaryLayout>
  );
};

export default CommissaryInventory;
